import React, { useState, useEffect } from "react";
import { Plus, Globe, Trash2, ShieldCheck, Clock } from "lucide-react";
import api from "../utils/api";
import { useToast } from "../context/ToastContext";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import PageHeader from "../components/PageHeader";

const DomainManagement = ({ activeAiAgentId }) => {
  const { showToast } = useToast();
  const [domains, setDomains] = useState([]);
  const [newDomain, setNewDomain] = useState("");
  const [loading, setLoading] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [domainToDelete, setDomainToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadDomains();
  }, [activeAiAgentId]);

  const loadDomains = async () => {
    try {
      const response = await api.getDomains();
      setDomains(response);
    } catch (error) {
      console.error("Error loading domains:", error);
    }
  };

  const addDomain = async () => {
    if (!newDomain.trim()) return;

    // Premium proactive check: clean input and check if it already exists in state
    const cleaned = newDomain.trim().toLowerCase().replace(/^(https?:\/\/)?(www\.)?/, '').replace(/\/$/, '');
    const isDuplicate = domains.some(d => {
      const dCleaned = d.domain.toLowerCase().replace(/^(https?:\/\/)?(www\.)?/, '').replace(/\/$/, '');
      return dCleaned === cleaned;
    });

    if (isDuplicate) {
      showToast(`Domain '${newDomain}' is already authorized!`, "error");
      return;
    }

    setLoading(true);
    try {
      await api.addDomain(newDomain);
      setNewDomain("");
      await loadDomains();
      showToast("Domain added successfully!", "success");
    } catch (error) {
      showToast(error.message || "Failed to add domain", "error");
    } finally {
      setLoading(false);
    }
  };

  const deleteDomain = (domainId) => {
    setDomainToDelete(domainId);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!domainToDelete) return;
    setIsDeleting(true);
    try {
      await api.deleteDomain(domainToDelete);
      await loadDomains();
      showToast("Domain deleted", "success");
      setIsDeleteModalOpen(false);
    } catch (error) {
      showToast("Failed to delete domain", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFCFD] pt-4 pb-8 px-8 lg:pt-6 lg:pb-12 lg:px-12 animate-fadeIn flex justify-center items-start border-t border-gray-100">
      <div className="w-full max-w-4xl space-y-12">
        {/* Header Section */}
        <PageHeader 
          title="Domain Security"
          description="Govern the architectural boundaries of your AI interface with cryptographic origin validation."
        />

        <div className="grid gap-8 animate-slideIn">
          {/* Add New Domain Card */}
          <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-xl shadow-gray-100/50 relative overflow-hidden">
            <div className="flex items-center gap-3 mb-8">
              <Plus size={24} className="text-gray-900" />
              <div>
                <h3 className="text-xs font-bold uppercase tracking-widest text-gray-900 mb-1">Authorize New Domain</h3>
                <p className="text-[11px] font-medium text-gray-400 m-0">Add a new origin to the allowlist</p>
              </div>
            </div>

             <div className="flex flex-col md:flex-row gap-4 items-center w-full">
              <div className="flex-1 w-full group">
                <input
                  type="text"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  placeholder="e.g., example.com"
                  className="w-full px-5 py-4 bg-gray-50 border-0 rounded-2xl focus:ring-2 focus:ring-gray-900 text-gray-900 placeholder:text-gray-400 font-mono text-sm transition-all"
                  onKeyPress={(e) => e.key === "Enter" && addDomain()}
                />
              </div>
              <button
                onClick={addDomain}
                disabled={loading || !newDomain.trim()}
                className="flex-shrink-0 flex items-center justify-center gap-2 px-10 py-4 bg-gray-900 text-white rounded-2xl hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-bold text-sm uppercase tracking-widest shadow-md active:scale-95"
              >
                {loading ? "Authorizing..." : "Add Domain"}
              </button>
            </div>
          </div>

          {/* Allowed Domains List Card */}
          <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-xl shadow-gray-100/50">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <ShieldCheck size={24} className="text-blue-600" />
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-widest text-gray-900 mb-1">Allowed Domains</h3>
                  <p className="text-[11px] font-medium text-gray-400 m-0">{domains.length} domains currently authorized</p>
                </div>
              </div>
            </div>

            {domains.length === 0 ? (
              <div className="text-center py-16 bg-gray-50/50 rounded-3xl border-2 border-dashed border-gray-100 group transition-all hover:border-blue-200">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm group-hover:scale-110 transition-transform duration-500">
                  <Globe size={32} className="text-gray-300" />
                </div>
                <p className="text-gray-400 font-medium">No domains added yet.</p>
                <p className="text-xs text-gray-400 mt-1 px-8">Add your first domain above to enable the chat widget on your site.</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {domains.map((domain) => (
                  <div
                    key={domain.id}
                    className="group flex items-center justify-between p-5 border border-gray-100 rounded-2xl bg-white hover:bg-gray-50 transition-all hover:shadow-md"
                  >
                    <div className="flex items-center gap-4">
                      <Globe size={24} className="text-blue-500" />
                      <div>
                        <div className="text-xs font-bold text-gray-900 mb-1">
                          {domain.domain}
                        </div>
                        <div className="flex items-center gap-1.5 text-[10px] text-gray-400 font-medium uppercase tracking-wider">
                          <Clock size={10} />
                          Authorized on {new Date(domain.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => deleteDomain(domain.id)}
                      className="p-3 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>


      </div>
      <DeleteConfirmModal 
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        isLoading={isDeleting}
        title="Sever Domain Link?"
        message="This action will permanently revoke cryptographic access for this origin. Are you prepared to proceed with the deletion?"
      />
    </div>
  );
};

export default DomainManagement;
